// ///////////////////////////////////////////////////////////
// tbAudio: The engine part dealing with audio and sounds
//
// Copyright 2013-2015 CoolGames
// Last Changed: 26-11-2015
// ///////////////////////////////////////////////////////////
import { LoadManager } from "../code/asset_loading/LoadManager";
import { tbBrowser } from "./tbBrowser";
import { tbLoader } from "./tbLoader";
import { tbSoundInstance } from "./tbSoundInstance";
var webkitAudioContext = undefined;
var TBAudio = /** @class */ (function () {
    function TBAudio() {
        this.usingWebAudio = false;
        this.usingHTMLAudio = false;
        this.audioSupported = false;
        this.maxNoOfChannels = 10;
        this._maxConSnds = -1;
        this._maxConSndsPolicy = "stop_lowest_prio";
        this._activeInstances = [];
        this._inactiveSources = {};
        this._tagVolumeMap = {};
        this._tagVolumeMapBackup = {};
        this._fadingTags = [];
        this._noPlayingInstances = 0;
        this._gainNodes = {};
        // check webAudio
        var audioObj;
        var ContextClass = (typeof AudioContext !== "undefined") ? AudioContext : ((typeof webkitAudioContext !== "undefined") ? webkitAudioContext : undefined);
        if (ContextClass) {
            this.usingWebAudio = true;
        } // check html5 audio
        else if (typeof Audio !== "undefined") {
            try {
                if (typeof (new Audio()).canPlayType !== "undefined") {
                    this.usingHTMLAudio = true;
                }
            }
            catch (ignore) { }
        }
        this.audioSupported = this.usingWebAudio || this.usingHTMLAudio;
        if (this.usingHTMLAudio && tbBrowser.browser.stockandroid) {
            this.maxConcurrentSounds(1);
        }
        // check browser codec support
        if (this.audioSupported) {
            try {
                audioObj = new Audio();
                this._codecs = {
                    "ogg": !!audioObj.canPlayType("audio/ogg; codecs=\"vorbis\"").replace(/^no$/, ""),
                    "mp3": !!audioObj.canPlayType("audio/mpeg;").replace(/^no$/, ""),
                    "opus": !!audioObj.canPlayType("audio/ogg; codecs=\"opus\"").replace(/^no$/, ""),
                    "wav": !!audioObj.canPlayType("audio/wav; codecs=\"1\"").replace(/^no$/, ""),
                    "m4a": !!(audioObj.canPlayType("audio/x-m4a;") || audioObj.canPlayType("audio/aac;")).replace(/^no$/, ""),
                    "mp4": !!(audioObj.canPlayType("audio/x-mp4;") || audioObj.canPlayType("audio/aac;")).replace(/^no$/, ""),
                    "weba": !!audioObj.canPlayType("audio/webm; codecs=\"vorbis\"").replace(/^no$/, "")
                };
            }
            catch (ignore) {
                // To solve an issue with the Edge browser on which Audio does not seem to be supported
                this._codecs = {
                    "ogg": false,
                    "mp3": true,
                    "opus": false,
                    "wav": false,
                    "m4a": false,
                    "mp4": false,
                    "weba": false
                };
                if (!PROD) {
                    console.log("Cannot create Audio object to determine codec support. Only supporting MP3.");
                }
            }
        }
        if (this.usingWebAudio) {
            console.log("tbAudio::usingWebAudio");
            // set-up AudioContext
            this._context = new ContextClass();
            this._createGain = (typeof this._context.createGain === "function") ? function () { return this._context.createGain(); } : ((typeof this._context.createGainNode === "function") ? function () { return this._context.createGainNode(); } : function () { });
            // create master gain to which all other nodes connect
            this._masterGain = this._createGain();
            if (this._masterGain === undefined) {
                this.usingHTMLAudio = true;
                this.loadAudioFile = this._loadHTMLAudio.bind(this);
            }
            this._masterGain.connect(this._context.destination);
            this._gainNodes["master"] = this._masterGain;
            // set-up loading function
            this.loadAudioFile = this._loadWebAudio.bind(this);
        }
        else if (this.usingHTMLAudio) {
            console.log("tbAudio::usingHTMLAudio");
            this.loadAudioFile = this._loadHTMLAudio.bind(this);
        }
        else {
            console.warn("No supported way for loading audio files");
            this.loadAudioFile = function () {
                return Promise.resolve();
            };
        }
    }
    Object.defineProperty(TBAudio.prototype, "tagVolumeMap", {
        get: function () { return this._tagVolumeMap; },
        enumerable: false,
        configurable: true
    });
    TBAudio.prototype.maxConcurrentSounds = function (max) {
        if (max !== undefined) {
            this._maxConSnds = max;
        }
        return this._maxConSnds;
    };
    TBAudio.prototype.maxConcurrentSoundsPolicy = function (policy) {
        if (policy !== undefined) {
            this._maxConSndsPolicy = policy;
        }
        return this._maxConSndsPolicy;
    };
    TBAudio.prototype.handleStep = function (timePassed) {
        var handler, tag, value;
        var _loop_1 = function (i) {
            var instance = this_1._activeInstances[i];
            if (instance && instance.enginePaused === 0) {
                if (!instance.paused) {
                    instance.timer += timePassed;
                    // if instance playback is delayed, check if delay has passed
                    if (instance.delayed && instance.timer >= instance.startDelay) {
                        instance.delayed = false;
                        this_1._startPlaying(instance, instance.startOffset);
                    } // if instance has reached end sound loop or deactivate
                    // not required for web audio because of "onended" event
                    else if (instance.playing && this_1.usingHTMLAudio) {
                        if (this_1.getPosition(instance.id) >= instance.duration) {
                            if (!instance.looping) {
                                instance.source.pause();
                                tbAudio._handleEndSound(instance);
                            }
                            else {
                                // web audio is looped automatically, for HTML audio playing pointer has to be reset to start of sound
                                // check and when necessary wait till audio is in "canPlayThrough" state before updating position
                                try {
                                    instance.source.pause();
                                    instance.source.currentTime = instance.startOffset / 1000;
                                    if (instance.source.readyState === 4) {
                                        instance.source.play();
                                    }
                                    else {
                                        handler = (function () {
                                            var inst = instance;
                                            return {
                                                ready: function () {
                                                    inst.source.play();
                                                    inst.source.removeEventListener("canplaythrough", handler.ready, false);
                                                }
                                            };
                                        }());
                                        instance.source.addEventListener("canplaythrough", handler.ready, false);
                                    }
                                }
                                catch (e) {
                                    if (!PROD) {
                                        console.log("error in step when position > duration: " + e.name + ", " + e.message);
                                    }
                                }
                            }
                        }
                    }
                } // if instance paused for specific duration, check if duration has passed
                else if (instance.pauseDuration) {
                    instance.pauseTimer += timePassed;
                    if (instance.pauseTimer >= instance.pauseDuration) {
                        this_1.resume(instance.id);
                    }
                }
            }
        };
        var this_1 = this;
        for (var i = 0; i < this._activeInstances.length; i += 1) {
            _loop_1(i);
        }
        // update gain on sounds that are fading
        for (var i = this._fadingTags.length - 1; i >= 0; i -= 1) {
            tag = this._fadingTags[i];
            if (!this.isPaused(tag.id) && tag.enginePaused === 0) {
                tag.timer += timePassed;
                if (tag.timer >= tag.duration) {
                    tbAudio.setGain(tag.id, tag.targetValue);
                    if (this._tagVolumeMapBackup[tag.id] !== undefined) {
                        this._tagVolumeMapBackup[tag.id] = tag.targetValue;
                    }
                    if (tag.callback) {
                        tag.callback();
                    }
                    this._fadingTags.splice(i, 1);
                }
                else {
                    value = tag.ease(tag.timer, tag.start, tag.targetValue - tag.start, tag.duration);
                    tbAudio.setGain(tag.id, value);
                    if (this._tagVolumeMapBackup[tag.id] !== undefined) {
                        this._tagVolumeMapBackup[tag.id] = value;
                    }
                }
            }
        }
    };
    TBAudio.prototype._deactivateInstance = function (instance) {
        // add audio object to inactive sound queue for reuse (without loading)
        if (!this._inactiveSources[instance.sound.sprite.name]) {
            this._inactiveSources[instance.sound.sprite.name] = [instance.source];
        }
        else if (this._inactiveSources[instance.sound.sprite.name].length < this.maxNoOfChannels) {
            this._inactiveSources[instance.sound.sprite.name].push(instance.source);
        }
    };
    TBAudio.prototype.addInactiveSources = function (tag, sources) {
        this._inactiveSources[tag] = sources;
    };
    TBAudio.prototype._findActiveInstances = function (tag) {
        var i, instance, retVal;
        retVal = [];
        for (i = 0; i < this._activeInstances.length; i += 1) {
            instance = this._activeInstances[i];
            if (instance) {
                if (instance.tags.indexOf(tag) >= 0) {
                    retVal.push(instance);
                }
            }
        }
        return retVal;
    };
    TBAudio.prototype._checkPlayPrio = function (sound) {
        if (this._maxConSnds > 0 && this._noPlayingInstances >= this._maxConSnds) {
            var inst = void 0, lowest = void 0;
            switch (this._maxConSndsPolicy) {
                case "cancel_new":
                    return false;
                case "stop_lowest_prio":
                    // find currently playing
                    for (var i = 0; i < this._activeInstances.length; i += 1) {
                        inst = this._activeInstances[i];
                        if (inst && inst.playing && !inst.paused) {
                            if (lowest === undefined || lowest.priority < inst.priority) {
                                lowest = inst;
                            }
                        }
                    }
                    if (lowest.priority > sound._priority) {
                        this.stop(lowest.id);
                        return true;
                    }
                    return false;
                    break;
            }
        }
        return true;
    };
    TBAudio.prototype._connectWebAudioNode = function (instance) {
        var t, node, totGain = 1;
        for (t = 0; t < instance.tags.length; t += 1) {
            if (tbAudio._tagVolumeMap[instance.tags[t]] !== undefined) {
                totGain *= tbAudio._tagVolumeMap[instance.tags[t]];
            }
        }
        node = this._createGain();
        node.gain.value = totGain;
        node.connect(this._masterGain);
        this._gainNodes[instance.id] = node;
        instance.source.connect(node);
    };
    TBAudio.prototype._disconnectWebAudioNode = function (instance) {
        instance.source.disconnect(0);
        if (this._gainNodes[instance.id]) {
            this._gainNodes[instance.id].disconnect(0);
            delete this._gainNodes[instance.id];
        }
    };
    TBAudio.prototype._createSoundSourceInstance = function (sound) {
        if (!sound.sprite || !sound.sprite.audio) {
            if (!PROD) {
                console.warn("Attempt to play sound \"".concat(sound.name, "\" that is not loaded."));
            }
            return;
        }
        var source;
        if (this.usingWebAudio) {
            // create a new source node and add the node to the graph
            source = this._context.createBufferSource();
            source.buffer = sound.sprite.audio;
            source.loopStart = sound.startOffset / 1000;
            source.loopEnd = (sound.startOffset + sound.duration) / 1000;
            return source;
        }
        if (this.usingHTMLAudio) {
            if (sound.sprite.audio.cloneNode === undefined || sound.sprite.audio.cloneNode === null) {
                // When the audio object is errored, we stop using audio as a fallback to prevent the game from crashing
                tbAudio.usingWebAudio = false;
                tbAudio.usingHTMLAudio = false;
                tbAudio.audioSupported = false;
                return;
            }
            // clone audio node
            source = sound.sprite.audio.cloneNode(true);
            source.volume = 0;
            return source;
        }
    };
    TBAudio.prototype._createSoundInstance = function (sound) {
        var instance;
        if (this.usingWebAudio) {
            var s = this._createSoundSourceInstance(sound);
            if (s) {
                instance = new tbSoundInstance(sound, s);
            }
        }
        else if (this.usingHTMLAudio) {
            // check if any audio sources are currently unused,
            // otherwise create new audio source
            var sources = this._inactiveSources[sound.sprite.name];
            if (!sources) {
                return;
            }
            if (sources.length > 0) {
                instance = new tbSoundInstance(sound, sources.pop());
            }
            else {
                var s = this._createSoundSourceInstance(sound);
                if (s) {
                    instance = new tbSoundInstance(sound, s);
                }
            }
        }
        // add sound instance to activeInstance array
        if (instance) {
            if (this.usingWebAudio) {
                this._connectWebAudioNode(instance);
            }
            for (var i = 0; i < this._activeInstances.length; i += 1) {
                if (this._activeInstances[i] === undefined) {
                    this._activeInstances[i] = instance;
                    return instance;
                }
            }
            this._activeInstances.push(instance);
        }
        return instance;
    };
    TBAudio.prototype._selectFormat = function (filenames) {
        for (var i = 0; i < filenames.length; i += 1) {
            var ext = filenames[i].split(".").pop();
            if (this._codecs[ext]) {
                return filenames[i];
            }
        }
        return false;
    };
    TBAudio.prototype._loadHTMLAudio = function (sprite, urls, moment) {
        var _this = this;
        LoadManager.logLoadInfo("TBAudio::loadHTMLAudio", urls);
        return new Promise(function (resolve, reject) {
            var url = _this._selectFormat(urls);
            var onerror = function () {
                tbLoader.loadFailed(moment);
                reject("Audio did not load properly " + url.toString());
            };
            var onload = function () {
                if (sprite.audio.cloneNode === undefined) {
                    onerror();
                    return;
                }
                var audioObjClone;
                sprite.loaded = true;
                tbLoader.loadComplete(moment);
                // round up the duration when using HTML5 Audio to account for the lower precision
                sprite.duration = Math.ceil(sprite.audio.duration * 1000);
                // remove event handlers
                sprite.audio.removeEventListener("canplaythrough", onload, false);
                sprite.audio.removeEventListener("error", onerror, false);
                // already create one copy of source so it will be pre loaded
                audioObjClone = sprite.audio.cloneNode(true);
                tbAudio._inactiveSources[sprite.name].push(audioObjClone);
                LoadManager.logLoadInfo("HTMLAudio Load SUCCESS", url);
                resolve();
            };
            // Start loading
            if (url) {
                sprite.audio = new Audio();
                sprite.audio.src = url;
                sprite.audio.autoplay = false;
                sprite.audio.preload = "auto";
                sprite.audio.addEventListener("canplaythrough", onload, false);
                sprite.audio.addEventListener("error", onerror, false);
                sprite.audio.load();
            }
            else {
                onerror();
            }
        });
    };
    TBAudio.prototype._loadWebAudio = function (sprite, urls, moment) {
        LoadManager.logLoadInfo("TBAudio::loadWebAudio", urls);
        return new Promise(function (resolve, reject) {
            var _this = this;
            var url = tbAudio._selectFormat(urls), onerror = function () {
                reject("Audio did not load properly " + url);
            }, onload = function (buffer) {
                sprite.audio = buffer;
                sprite.duration = buffer.duration * 1000;
                sprite.loaded = true;
                tbLoader.loadComplete(moment);
                resolve();
            }, request = new XMLHttpRequest();
            // load the buffer from the URL
            request.open("GET", url, true);
            request.responseType = "arraybuffer";
            request.onload = function () {
                // decode the buffer into an audio source
                tbAudio._context.decodeAudioData(request.response, function (buffer) {
                    if (buffer) {
                        onload(buffer);
                    }
                }, onerror);
            };
            request.onerror = function () {
                // if there is an error, switch the sound to HTML Audio
                if (typeof Audio !== "undefined") {
                    tbAudio.usingWebAudio = false;
                    tbAudio.usingHTMLAudio = true;
                    tbAudio.loadAudioFile = _this._loadHTMLAudio.bind(_this);
                }
                reject("Audio did not load properly " + url);
            };
            request.send();
        });
    };
    // create and play a new soundInstance
    TBAudio.prototype.play = function (item, delay, fadeDuration, fadeEase, looping) {
        if (this._checkPlayPrio(item)) {
            var instance = this._createSoundInstance(item);
            if (!instance) {
                return -1;
            }
            instance.startDelay = delay || 0;
            instance.looping = looping || false;
            instance.delayed = (delay > 0);
            instance.fadeDuration = fadeDuration || 0;
            instance.fadeEase = fadeEase || function (t, b, c, d) { return (t === 0) ? b : c * Math.pow(2, 10 * (t / d - 1)) + b; };
            if (!instance.delayed) {
                this._startPlaying(instance, instance.startOffset);
            }
            return instance.id;
        }
        return -1;
    };
    TBAudio.prototype._startPlaying = function (instance, offset) {
        var position;
        if (typeof offset !== "number") {
            offset = 0;
        }
        this._updateGain(instance.id);
        if (instance.fadeDuration > 0) {
            var gain = this.getTagGain(instance.id);
            this.setGain(instance.id, 0);
            this.fade(instance.id, gain, instance.fadeDuration, instance.fadeEase);
            instance.fadeDuration = 0;
            instance.fadeEase = undefined;
        }
        if (this.usingWebAudio) {
            position = offset - instance.startOffset;
            instance.startTime = this._context.currentTime * 1000 - position;
            // This must be set before calling start, otherwise Firefox will not loop the sound
            instance.source.loop = instance.looping;
            instance.source.onended = function () { tbAudio._handleEndSound(instance); };
            try {
                // check for old function calls as well as new ones
                if (instance.source.start) {
                    // With this stop set, the sound will not loop, so it is only set when looping is false
                    var stop_1 = instance.looping ? undefined : (instance.duration - position) / 1000;
                    instance.source.start(0, offset / 1000, stop_1);
                }
                else if (instance.source.noteGrainOn) {
                    instance.source.noteGrainOn(0, offset / 1000, (instance.duration - position) / 1000);
                }
                instance.started = true;
                instance.playing = true;
                this._noPlayingInstances += 1;
                // Must be called after starting sound a second time, otherwise Chrome will not loop the sound
                instance.source.loop = instance.looping;
            }
            catch (e) {
                if (!PROD) {
                    console.log("error in starting sound: " + e.name + ", " + e.message);
                }
            }
        }
        else if (this.usingHTMLAudio) {
            if (instance.source.readyState !== 4) {
                // if not yet ready to play, wait till can play through then play
                var handler_1 = (function () {
                    var inst = instance;
                    return {
                        ready: function () {
                            inst.source.currentTime = offset / 1000;
                            inst.source.play();
                            inst.started = true;
                            inst.source.removeEventListener("canplaythrough", handler_1.ready, false);
                        }
                    };
                })();
                instance.source.addEventListener("canplaythrough", handler_1.ready, false);
            }
            else {
                // already ready so play immediately
                instance.source.currentTime = offset / 1000;
                instance.source.play();
                instance.started = true;
            }
            instance.playing = true;
            this._noPlayingInstances += 1;
        }
    };
    // resume (or start) active instances that match tag
    TBAudio.prototype.resume = function (tag, fadeDuration, fadeEase, enginePause) {
        var gain, fadingTag, source;
        var items = this._findActiveInstances(tag);
        for (var i = 0; i < items.length; i += 1) {
            var instance = items[i];
            if (((instance.paused || !instance.playing) && !enginePause) || (!instance.paused && enginePause)) {
                if (!enginePause) {
                    // check to see if instance was already fading before pause
                    for (i = this._fadingTags.length - 1; i >= 0; i -= 1) {
                        tag = this._fadingTags[i];
                        if (tag.id === instance.id) {
                            fadingTag = tag;
                            fadeDuration = 0;
                            fadeEase = undefined;
                            break;
                        }
                    }
                    instance.paused = false;
                    instance.fadeDuration = fadeDuration || 0;
                    instance.fadeEase = fadeEase || function (t, b, c, d) { return (t === 0) ? b : c * Math.pow(2, 10 * (t / d - 1)) + b; };
                    if (instance.fadedOnPause) {
                        if (fadeDuration === undefined) {
                            instance.fadeDuration = instance.fadedOnPause.duration;
                        }
                        if (fadeEase === undefined) {
                            instance.fadeEase = instance.fadedOnPause.ease;
                        }
                        gain = instance.fadedOnPause.gain;
                        instance.fadedOnPause = undefined;
                    }
                }
                if (this.usingWebAudio) {
                    // audio node must be recreated, after sound being stopped on pause it cannot be restarted
                    source = this._createSoundSourceInstance(instance.sound);
                    if (source) {
                        instance.source = source;
                        this._connectWebAudioNode(instance);
                    }
                }
                if (gain !== undefined) {
                    tbAudio.setGain(instance.id, gain);
                }
                // the progress in the sound already made before pausing must be given as a parameter
                this._startPlaying(instance, instance.startOffset + (instance.pausedPosition || 0));
                if (fadingTag !== undefined) {
                    tbAudio.setGain(instance.id, fadingTag.ease(fadingTag.timer, fadingTag.start, fadingTag.targetValue - fadingTag.start, fadingTag.duration));
                    tbAudio.fade(instance.id, fadingTag.targetValue, fadingTag.duration - fadingTag.timer, fadingTag.ease, fadingTag.callback);
                }
            }
        }
    };
    // pause playing active instances that match tag
    TBAudio.prototype.pause = function (tag, duration, fadeDuration, fadeEase, enginePause) {
        var items = this._findActiveInstances(tag);
        var _loop_2 = function (i) {
            var instance = items[i];
            if (!instance.paused) {
                instance.fadeDuration = fadeDuration || 0;
                if (instance.fadeDuration > 0) {
                    instance.fadeEase = fadeEase || function (t, b, c, d) { return (t === 0) ? b : c * Math.pow(2, 10 * (t / d - 1)) + b; };
                    // remember gain previous to fading so it can be reset on resume
                    instance.fadedOnPause = { gain: tbAudio.getGain(instance.id), duration: instance.fadeDuration, ease: instance.fadeEase };
                    tbAudio.fade(instance.id, 0, instance.fadeDuration, instance.fadeEase, function () { tbAudio.pause(instance.id, duration); });
                }
                else {
                    var position = this_2.getPosition(instance.id);
                    instance.pausedPosition = position;
                    if (!enginePause) {
                        instance.paused = true;
                        instance.pauseTimer = 0;
                        instance.pauseDuration = duration;
                        this_2._noPlayingInstances -= 1;
                    }
                    if (this_2.usingWebAudio) {
                        instance.source.onended = function () { };
                        if (instance.playing) {
                            if (instance.started) {
                                try {
                                    if (instance.source.stop) {
                                        instance.source.stop(0);
                                    }
                                    else if (instance.source.noteOff) {
                                        instance.source.noteOff(0);
                                    }
                                }
                                catch (e) {
                                    if (!PROD) {
                                        console.log("error in stopping sound for pause: " + e.name + ", " + e.message);
                                    }
                                }
                                instance.started = false;
                            }
                        }
                        this_2._disconnectWebAudioNode(instance);
                    }
                    else if (this_2.usingHTMLAudio) {
                        instance.source.pause();
                    }
                }
            }
        };
        var this_2 = this;
        for (var i = 0; i < items.length; i += 1) {
            _loop_2(i);
        }
    };
    TBAudio.prototype._handleEndSound = function (instance) {
        if (this._tagVolumeMap[instance.id]) {
            delete this._tagVolumeMap[instance.id];
        }
        if (!instance.paused) {
            this._noPlayingInstances -= 1;
        }
        if (this.usingWebAudio) {
            instance.started = false;
            instance.playing = false;
            this._disconnectWebAudioNode(instance);
        }
        else if (this.usingHTMLAudio) {
            // store source for future use
            this._deactivateInstance(instance);
        }
        // remove instance from active instances list
        this._activeInstances[this._activeInstances.indexOf(instance)] = undefined;
    };
    TBAudio.prototype.stop = function (tag, fadeDuration, fadeEase) {
        var items = this._findActiveInstances(tag);
        var _loop_3 = function (i) {
            var instance = items[i];
            instance.fadeDuration = fadeDuration || 0;
            if (instance.fadeDuration > 0) {
                instance.fadeEase = fadeEase || function (t, b, c, d) { return (t === 0) ? b : c * Math.pow(2, 10 * (t / d - 1)) + b; };
                tbAudio.fade(instance.id, 0, instance.fadeDuration, instance.fadeEase, function () { tbAudio.stop(instance.id); });
            }
            else {
                // remove instances from volume map
                if (this_3._tagVolumeMap[instance.id]) {
                    delete this_3._tagVolumeMap[instance.id];
                }
                // update number of sounds playing
                if (instance.playing && !instance.paused) {
                    this_3._noPlayingInstances -= 1;
                }
                if (this_3.usingWebAudio) {
                    if (instance.playing && !instance.paused && !instance.delayed) {
                        // make sure sound has already started otherwise an error may occur
                        if (instance.started) {
                            try {
                                if (instance.source.stop) {
                                    instance.source.stop(0);
                                }
                                else if (instance.source.noteOff) {
                                    instance.source.noteOff(0);
                                }
                            }
                            catch (e) {
                                if (!PROD) {
                                    console.log("error in stopping sound: " + e.name + ", " + e.message);
                                }
                            }
                            instance.started = false;
                        }
                        this_3._disconnectWebAudioNode(instance);
                    }
                }
                else if (this_3.usingHTMLAudio) {
                    if (!instance.delayed) {
                        instance.source.pause();
                    }
                    // reset instance and store for future use
                    this_3._deactivateInstance(instance);
                }
                // remove instance from active instances list
                this_3._activeInstances[this_3._activeInstances.indexOf(instance)] = undefined;
                instance.playing = false;
            }
        };
        var this_3 = this;
        for (var i = 0; i < items.length; i += 1) {
            _loop_3(i);
        }
    };
    TBAudio.prototype.enginePause = function (tag) {
        var i, instance, items = this._findActiveInstances(tag);
        for (i = 0; i < items.length; i += 1) {
            instance = items[i];
            if (instance.enginePaused === 0) {
                tbAudio.pause(instance.id, 0, 0, null, true);
            }
            instance.enginePaused += 1;
        }
        for (i = 0; i < this._fadingTags.length; i += 1) {
            if (tag === "master" || this._fadingTags[i].id === tag) {
                this._fadingTags[i].enginePaused += 1;
            }
        }
    };
    TBAudio.prototype.engineResume = function (tag) {
        var i, instance, items = this._findActiveInstances(tag);
        for (i = 0; i < items.length; i += 1) {
            instance = items[i];
            instance.enginePaused = Math.max(instance.enginePaused - 1, 0);
            if (instance.enginePaused === 0) {
                tbAudio.resume(instance.id, 0, null, true);
            }
        }
        for (i = 0; i < this._fadingTags.length; i += 1) {
            if (tag === "master" || this._fadingTags[i].id === tag) {
                this._fadingTags[i].enginePaused = Math.max(this._fadingTags[i].enginePaused - 1, 0);
            }
        }
    };
    TBAudio.prototype.engineResumeForced = function (tag) {
        var i, items = this._findActiveInstances(tag);
        for (i = 0; i < items.length; i += 1) {
            items[i].enginePaused = 0;
            tbAudio.resume(items[i].id, 0, null, true);
        }
        for (i = 0; i < this._fadingTags.length; i += 1) {
            if (tag === "master" || this._fadingTags[i].id === tag) {
                this._fadingTags[i].enginePaused = 0;
            }
        }
    };
    TBAudio.prototype.fade = function (tag, targetValue, duration, easeFunction, callback) {
        var i;
        for (i = 0; i < this._fadingTags.length; i += 1) {
            if (this._fadingTags[i].id === tag) {
                this._fadingTags.splice(i, 1);
                break;
            }
        }
        this._fadingTags.push({ id: tag, targetValue: targetValue, ease: easeFunction || function (t, b, c, d) { return (t === d) ? b + c : c * (-Math.pow(2, -10 * t / d) + 1) + b; },
            duration: duration, timer: 0, start: this.getTagGain(tag), callback: callback, enginePaused: 0 });
    };
    TBAudio.prototype.mute = function (tag) {
        var oldVolume;
        if (this._tagVolumeMapBackup[tag] === undefined) {
            oldVolume = (this._tagVolumeMap[tag] !== undefined) ? this._tagVolumeMap[tag] : 1;
            // set tag gain value to 0
            this.setGain(tag, 0);
            // store previous gain value of tag
            this._tagVolumeMapBackup[tag] = oldVolume;
        }
    };
    TBAudio.prototype.unmute = function (tag) {
        // if a previous gain value was stored, reset tag gain to this value
        if (this._tagVolumeMapBackup[tag] !== undefined) {
            this.setGain(tag, this._tagVolumeMapBackup[tag]);
            delete this._tagVolumeMapBackup[tag];
        }
    };
    TBAudio.prototype.position = function (tag, position) {
        var source, setPos;
        var items = this._findActiveInstances(tag);
        if (!isNaN(position) && position >= 0) {
            var _loop_4 = function (i) {
                var instance = items[i];
                position = position % instance.duration;
                if (this_4.usingWebAudio) {
                    // cannot go back in buffer so stop current buffer and create new one that starts at given position
                    if (!instance.paused) {
                        instance.source.onended = function () { };
                        // make sure sound is already started before trying to stop it
                        if (instance.started) {
                            try {
                                if (instance.source.stop) {
                                    instance.source.stop(0);
                                }
                                else if (instance.source.noteOff) {
                                    instance.source.noteOff(0);
                                }
                            }
                            catch (e) {
                                if (!PROD) {
                                    console.log("error in stopping sound for position: " + e.name + ", " + e.message);
                                }
                            }
                            instance.started = false;
                        }
                        this_4._disconnectWebAudioNode(instance);
                        this_4._noPlayingInstances -= 1;
                        // create new buffer and start playing from requested position
                        source = this_4._createSoundSourceInstance(instance.sound);
                        if (source) {
                            instance.source = source;
                            this_4._connectWebAudioNode(instance);
                            this_4._startPlaying(instance, instance.startOffset + position);
                        }
                    }
                    else {
                        instance.pausedPosition = position;
                    }
                }
                else if (this_4.usingHTMLAudio) {
                    // check and when necessary wait for sound to be "canPlayThrough" then jump to requested position
                    if (instance.source.readyState === 4) {
                        instance.source.currentTime = (instance.startOffset + position) / 1000;
                    }
                    else {
                        (setPos = function () {
                            var inst = instance, pos = position;
                            return {
                                handler: function () {
                                    inst.source.currentTime = (inst.startOffset + pos) / 1000;
                                    inst.source.removeEventListener("canplaythrough", setPos.handler, false);
                                }
                            };
                        }());
                        instance.source.addEventListener("canplaythrough", setPos.handler, false);
                    }
                }
            };
            var this_4 = this;
            for (var i = 0; i < items.length; i++) {
                _loop_4(i);
            }
        }
    };
    TBAudio.prototype.rewind = function (tag) {
        tbAudio.position(tag, 0);
    };
    TBAudio.prototype.setLooping = function (tag, looping) {
        var i, items = this._findActiveInstances(tag);
        for (i = 0; i < items.length; i += 1) {
            items[i].looping = looping;
            if (this.usingWebAudio) {
                // set property on buffer so webaudio api will automatically take care of the looping
                items[i].source.loop = looping;
            }
        }
    };
    TBAudio.prototype.setPriority = function (tag, priority) {
        var i, items = this._findActiveInstances(tag);
        for (i = 0; i < items.length; i += 1) {
            items[i].priority = priority;
        }
    };
    TBAudio.prototype.getInstances = function (tag) {
        var i, instance, retVal = [];
        for (i = 0; i < this._activeInstances.length; i += 1) {
            instance = this._activeInstances[i];
            if (instance) {
                if (instance.tags.indexOf(tag) >= 0) {
                    retVal.push(instance.id);
                }
            }
        }
        return retVal;
    };
    TBAudio.prototype.getTagGain = function (tag) {
        if (this._tagVolumeMap[tag] !== undefined) {
            return this._tagVolumeMap[tag];
        }
        return 1;
    };
    TBAudio.prototype.getGain = function (id) {
        var t, totGain = 1, instance = this._findActiveInstances(id)[0];
        if (instance) {
            for (t = 0; t < instance.tags.length; t += 1) {
                if (this._tagVolumeMap[instance.tags[t]] !== undefined) {
                    totGain *= this._tagVolumeMap[instance.tags[t]];
                }
            }
        }
        return Math.round(100 * totGain) / 100;
    };
    TBAudio.prototype.setGain = function (tag, volume) {
        var i, instance, t, totGain = 1, items = this._findActiveInstances(tag);
        // update tagVolumeMap
        this._tagVolumeMap[tag] = volume;
        // unmute sound if was muted
        if (this._tagVolumeMapBackup[tag]) {
            delete this._tagVolumeMapBackup[tag];
        }
        // update volume of all instances that have tag
        for (i = 0; i < items.length; i += 1) {
            instance = items[i];
            if (instance.tags.indexOf(tag) >= 0) {
                for (t = 0; t < instance.tags.length; t += 1) {
                    if (this._tagVolumeMap[instance.tags[t]] !== undefined) {
                        totGain *= this._tagVolumeMap[instance.tags[t]];
                    }
                }
                totGain = Math.round(100 * totGain) / 100;
                if (this.usingWebAudio && this._gainNodes[instance.id]) {
                    this._gainNodes[instance.id].gain.value = totGain;
                }
                else if (this.usingHTMLAudio) {
                    instance.source.volume = totGain;
                }
            }
        }
    };
    TBAudio.prototype._updateGain = function (tag) {
        var i, instance, t, totGain = 1, items = this._findActiveInstances(tag);
        // update volume of all instances that have tag
        for (i = 0; i < items.length; i += 1) {
            instance = items[i];
            for (t = 0; t < instance.tags.length; t += 1) {
                if (this._tagVolumeMap[instance.tags[t]] !== undefined) {
                    totGain *= this._tagVolumeMap[instance.tags[t]];
                }
            }
            totGain = Math.round(100 * totGain) / 100;
            if (this.usingWebAudio) {
                this._gainNodes[instance.id].gain.value = totGain;
            }
            else if (this.usingHTMLAudio) {
                instance.source.volume = totGain;
            }
        }
    };
    TBAudio.prototype.getTags = function (id) {
        var i, instance, t, tag, items = this._findActiveInstances(id), retVal = [];
        for (i = 0; i < items.length; i += 1) {
            instance = items[i];
            for (t = 0; t < instance.tags.length; t += 1) {
                tag = instance.tags[t];
                if (retVal.indexOf(tag) < 0) {
                    retVal.push(tag);
                }
            }
        }
        return retVal;
    };
    TBAudio.prototype.addTags = function (id, tags) {
        var items = this._findActiveInstances(id);
        for (var i = 0; i < items.length; i += 1) {
            var instance = items[i];
            tags = [].concat(tags);
            for (var t = 0; t < tags.length; t += 1) {
                if (instance.tags.indexOf(tags[t]) < 0) {
                    instance.tags.push(tags[t]);
                }
            }
        }
        this._updateGain(id);
    };
    TBAudio.prototype.removeTags = function (id, tags) {
        var i, instance, t, tag, idx, items = this._findActiveInstances(id);
        for (i = 0; i < items.length; i += 1) {
            instance = items[i];
            tags = [].concat(tags);
            for (t = 0; t < tags.length; t += 1) {
                tag = tags[t];
                if (tag !== instance.id && tag !== "master") {
                    idx = instance.tags.indexOf(tag);
                    if (idx >= 0) {
                        instance.tags.splice(idx, 1);
                    }
                }
            }
        }
        return instance.tags;
    };
    TBAudio.prototype.isPaused = function (id) {
        var instance = this._findActiveInstances(id)[0];
        if (instance) {
            return instance.paused;
        }
    };
    TBAudio.prototype.isPlaying = function (id) {
        var instance = this._findActiveInstances(id)[0];
        if (instance) {
            return instance.playing;
        }
    };
    TBAudio.prototype.isActive = function (id) {
        var instance = this._findActiveInstances(id)[0];
        if (instance) {
            return true;
        }
        return false;
    };
    TBAudio.prototype.isLooping = function (id) {
        var instance = this._findActiveInstances(id)[0];
        if (instance) {
            return instance.looping;
        }
    };
    TBAudio.prototype.getFading = function (id) {
        var i;
        for (i = 0; i < this._fadingTags.length; i += 1) {
            if (this._fadingTags[i].id === id) {
                return this._fadingTags[i].targetValue;
            }
        }
        return -1;
    };
    TBAudio.prototype.getPriority = function (id) {
        var instance = this._findActiveInstances(id)[0];
        if (instance) {
            return instance.priority;
        }
    };
    TBAudio.prototype.getDuration = function (id) {
        var instance = this._findActiveInstances(id)[0];
        if (instance) {
            return instance.sound.duration;
        }
    };
    TBAudio.prototype.getPosition = function (id) {
        var instance = this._findActiveInstances(id)[0];
        if (instance) {
            if (this.usingWebAudio) {
                if (instance.paused) {
                    return instance.pausedPosition;
                }
                return (tbAudio._context.currentTime * 1000 - instance.startTime) % instance.duration;
            }
            if (tbAudio.usingHTMLAudio) {
                return Math.ceil(instance.source.currentTime * 1000 - instance.startOffset);
            }
        }
    };
    return TBAudio;
}());
// create the tbAudio object
export var tbAudio = new TBAudio();
